import { Vector2 } from "../../../common/vector2.js";
import { VfxUnlockChainOfferBatch } from "./vfx_unlock_chain_offer_item_spark_batch.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Vfx2DGlobalAtlas } from "../../core/rendering/textures/vfx_2d_global_atlas.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Emitter } from "../../core/emitters/emitter.js";
import { Vfx2DRuntime } from "../../vfx_2d_runtime.js";
import { randomPointOnCircleContour } from "../../../common/visual_fx_common_funcs.js";
import { GenericSpriteBatch } from "../../core/batching/generic_sprite_batch.js";
import { ColorMode } from "../../core/types/webgl_types.js";


export class VfxUnlockChainOfferTask implements IController, IVfxTask
{
  // --- Sparks Config ---
  public static readonly MAX_RADIUS = 400;
  public static readonly THICKNESS = 60;
  public static readonly DURATION_MS = 400;
  
  public static readonly RATE_OVER_TIME = 30;
  public static readonly SPAWN_COUNT = 15;
  public static readonly PARTICLE_LIFESPAN_MS = 200;
  
  public static readonly PARTICLE_COLOR = [ 1.0, 0.549, 0.156 ];
  public static readonly PARTICLE_SIZE = 50;
  public static readonly PARTICLE_IMAGE_ID = ImgID.SparkGlow3;

  public static readonly PARTICLE_INTENSITY_MIN = 1;
  public static readonly PARTICLE_INTENSITY_MAX = 2;

  // --- Glow Config ---
  public static readonly GLOW_IMAGE_ID = ImgID.CircleGlow5;
  public static readonly GLOW_COLOR = [ 1.0, 0.392, 0.078 ];
  public static readonly GLOW_MAX_ALPHA = 1;
  public static readonly GLOW_SIZE_MULTIPLIER = 3.5;
  public static readonly GLOW_FADE_OUT_BREAKPOINT = 0.7;
  public static readonly GLOW_ADDITIVE = 1;

  private emitter: Emitter;
  private batch: VfxUnlockChainOfferBatch;
  private glow_batch: GenericSpriteBatch;
  private glow_id: number = -1;
  private is_active = true;
  private uv_bounds: { min_x: number, min_y: number, max_x: number, max_y: number };
  
  private total_effect_duration: number;
  private on_finish?: () => void;

  constructor( center: Vector2, start_time: number, on_finish?: () => void )
  {
    this.on_finish = on_finish;
    this.batch = Vfx2DRuntime.onMainFront( VfxUnlockChainOfferBatch );
    this.glow_batch = Vfx2DRuntime.onMainFront( GenericSpriteBatch );
    
    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    this.uv_bounds = atlas_data.getUVRect( VfxUnlockChainOfferTask.PARTICLE_IMAGE_ID );

    this.emitter = new Emitter( center, start_time, VfxUnlockChainOfferTask.DURATION_MS, {
      rate_over_time: VfxUnlockChainOfferTask.RATE_OVER_TIME,
      count: VfxUnlockChainOfferTask.SPAWN_COUNT
    });

    this.total_effect_duration = VfxUnlockChainOfferTask.DURATION_MS + VfxUnlockChainOfferTask.PARTICLE_LIFESPAN_MS;

    this.glow_id = this.glow_batch.spawnSprite(
      VfxUnlockChainOfferTask.GLOW_IMAGE_ID,
      center,
      new Vector2( 0, 0 ),
      { x: VfxUnlockChainOfferTask.GLOW_COLOR[0], y: VfxUnlockChainOfferTask.GLOW_COLOR[1], z: VfxUnlockChainOfferTask.GLOW_COLOR[2] },
      0, // alpha
      0, // angle
      VfxUnlockChainOfferTask.GLOW_ADDITIVE,
      start_time,
      this.total_effect_duration,
      ColorMode.Mask
    );
  }

  public setPosition( pos: Vector2 ): void 
  {
    this.emitter.setPosition( pos );
  }

  public accelerate( factor: number, now: number ): void 
  {
    this.emitter.accelerate( factor, now );
  }

  public stop( behavior?: StopBehavior, fade_duration?: number ): void 
  {
    this.emitter.stop( behavior, fade_duration ?? 0 );
    this.is_active = false;
  }

  public onUpdate( now: number ): boolean 
  {
    if ( !this.is_active ) 
      return true;

    const age = now - this.emitter.start_time;

    if ( age >= this.total_effect_duration ) 
    {
      this.is_active = false;
      this.on_finish?.();
      return true;
    }

    const scale = LayoutScaler.getScale();
    const max_radius = VfxUnlockChainOfferTask.MAX_RADIUS * scale;
    const thickness = VfxUnlockChainOfferTask.THICKNESS * scale;
    const particle_size = VfxUnlockChainOfferTask.PARTICLE_SIZE * scale;

    const p_all = this.emitter.getProgress( now );

    if ( p_all < 1.0 )
    {
      this.emitter.process( now, ( spawn_pos, spawn_time ) => 
      {
        const p = this.emitter.getProgress( spawn_time );
        const current_radius = p * max_radius;

        const p_spawn = randomPointOnCircleContour( spawn_pos, current_radius, thickness );

        this.batch.spawn(
          p_spawn.x, p_spawn.y,
          particle_size,
          VfxUnlockChainOfferTask.PARTICLE_COLOR[0],
          VfxUnlockChainOfferTask.PARTICLE_COLOR[1],
          VfxUnlockChainOfferTask.PARTICLE_COLOR[2],
          spawn_time, VfxUnlockChainOfferTask.PARTICLE_LIFESPAN_MS,
          VfxUnlockChainOfferTask.PARTICLE_INTENSITY_MIN,
          VfxUnlockChainOfferTask.PARTICLE_INTENSITY_MAX,
          this.uv_bounds.min_x, this.uv_bounds.min_y,
          this.uv_bounds.max_x, this.uv_bounds.max_y
        );
      });
    }



    if ( this.glow_id !== -1 )
    {
      const glow_progress = Math.max( 0, Math.min( 1.0, age / this.total_effect_duration ) );
      
      const glow_size = glow_progress * max_radius * VfxUnlockChainOfferTask.GLOW_SIZE_MULTIPLIER; 
      
      const alpha = ( glow_progress <= VfxUnlockChainOfferTask.GLOW_FADE_OUT_BREAKPOINT )
        ? 1.0
        : Math.max( 0.0, 1.0 - ( glow_progress - VfxUnlockChainOfferTask.GLOW_FADE_OUT_BREAKPOINT ) / ( 1.0 - VfxUnlockChainOfferTask.GLOW_FADE_OUT_BREAKPOINT ) );
      
      this.glow_batch.updateSpriteData(
        this.glow_id,
        this.emitter.position,
        new Vector2( glow_size, glow_size ),
        alpha * VfxUnlockChainOfferTask.GLOW_MAX_ALPHA,
        0
      );
    }

    return false;
  }
}
